import { useEffect } from 'react';

/**
 * Custom hook สำหรับเพิ่ม keyboard navigation ให้กับ modal ที่มีตาราง
 * @param {boolean} showModal - สถานะการแสดง modal
 * @param {Array} filteredDataList - ข้อมูลที่กรองแล้ว
 * @param {number} selectedRowIndex - index ของแถวที่เลือก
 * @param {Function} setSelectedRowIndex - ฟังก์ชันสำหรับตั้งค่า selectedRowIndex
 * @param {Function} onRowSelect - ฟังก์ชันสำหรับเลือกแถว
 * @param {Function} handleClose - ฟังก์ชันสำหรับปิด modal
 * @param {Object} options - ตัวเลือกเพิ่มเติม
 * @param {Function} options.customEnterHandler - ฟังก์ชัน Enter แบบกำหนดเอง
 * @param {Function} options.customSpaceHandler - ฟังก์ชัน Space แบบกำหนดเอง
 * @param {Array} options.additionalKeys - คีย์เพิ่มเติม [{ key: 'keyName', handler: function }]
 * @param {boolean} options.enableSpace - เปิดใช้งาน Space key (default: false)
 * @param {boolean} options.includeHighlightedOnEnter - เพิ่มสินค้าที่ highlight เข้าในรายการเมื่อกด Enter (สำหรับ checkbox modal)
 * @param {Array} options.selectedItems - รายการที่เลือกแล้ว (สำหรับ checkbox modal)
 */
export const useModalKeyboardNavigation = (
    showModal,
    filteredDataList,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelect,
    handleClose,
    options = {}
) => {
    const {
        customEnterHandler,
        customSpaceHandler,
        additionalKeys = [],
        enableSpace = false,
        includeHighlightedOnEnter = false,
        selectedItems = []
    } = options;
    useEffect(() => {
        const handleKeyDown = (event) => {
            // ทำงานเฉพาะเมื่อ modal เปิดอยู่
            if (!showModal) return;

            // ป้องกันการทำงานถ้า focus อยู่ใน input search หรือ select
            if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                // ยกเว้น Enter, ESC, F5 และ ArrowDown ที่ยังให้ทำงานได้ใน input/select
                if (event.key !== 'Enter' && event.key !== 'Escape' && event.key !== 'F5' && event.key !== 'ArrowDown') {
                    return;
                }
            }

            if (filteredDataList.length === 0) return;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex > 0 ? prevIndex - 1 : filteredDataList.length - 1;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        scrollToRow(newIndex);
                        return newIndex;
                    });
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    // ถ้าอยู่ใน input/select และกดลูกศรลง ให้ focus ไปที่ตารางและเลือกแถวแรก
                    if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                        setSelectedRowIndex(0);
                        scrollToRow(0);
                        // เอา focus ออกจาก input/select
                        document.activeElement.blur();
                        return;
                    }
                    
                    setSelectedRowIndex(prevIndex => {
                        const newIndex = prevIndex < filteredDataList.length - 1 ? prevIndex + 1 : 0;
                        // เลื่อน scroll ไปยังแถวที่เลือก
                        scrollToRow(newIndex);
                        return newIndex;
                    });
                    break;

                case 'Enter':
                    event.preventDefault();
                    if (customEnterHandler) {
                        customEnterHandler(filteredDataList[selectedRowIndex], selectedRowIndex);
                    } else if (includeHighlightedOnEnter) {
                        // สำหรับ checkbox modal - รวมสินค้าที่ highlight เข้าไปด้วย
                        let finalSelectedItems = [...selectedItems];
                        
                        if (filteredDataList[selectedRowIndex]) {
                            const highlightedItem = filteredDataList[selectedRowIndex];
                            const isHighlightedItemSelected = selectedItems.some(
                                (selected) => selected.Item_Id === highlightedItem.Item_Id
                            );
                            
                            // ถ้าสินค้าที่ highlight ยังไม่ได้อยู่ในรายการที่เลือก ให้เพิ่มเข้าไป
                            if (!isHighlightedItemSelected) {
                                finalSelectedItems = [...selectedItems, highlightedItem];
                            }
                        }
                        
                        // ส่งรายการสินค้าทั้งหมด
                        onRowSelect(finalSelectedItems);
                    } else if (filteredDataList[selectedRowIndex]) {
                        onRowSelect(filteredDataList[selectedRowIndex]);
                    }
                    break;

                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                    console.log('ESC pressed in Modal, closing modal');
                    handleClose();
                    return false;

                case ' ': // Spacebar
                    if (enableSpace || customSpaceHandler) {
                        event.preventDefault();
                        if (customSpaceHandler && filteredDataList[selectedRowIndex]) {
                            customSpaceHandler(filteredDataList[selectedRowIndex], selectedRowIndex);
                        }
                    }
                    break;

                case 'F5':
                    event.preventDefault();
                    const searchInput = document.querySelector('.modal.show input[type="text"]');
                    
                    // ถ้า focus อยู่ใน input/select อยู่แล้ว ให้เอา focus ออกเพื่อใช้ลูกศรได้
                    if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') {
                        document.activeElement.blur();
                        setSelectedRowIndex(0); // เลือกแถวแรก
                        scrollToRow(0);
                    } else {
                        // ถ้าไม่ได้ focus ใน input ให้ focus ช่องค้นหา
                        if (searchInput) {
                            searchInput.focus();
                        }
                    }
                    break;
                    
                default:
                    // Handle additional custom keys
                    const additionalKey = additionalKeys.find(k => k.key === event.key);
                    if (additionalKey) {
                        event.preventDefault();
                        additionalKey.handler(filteredDataList[selectedRowIndex], selectedRowIndex, event);
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown, true); 

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [showModal, filteredDataList, selectedRowIndex, setSelectedRowIndex, onRowSelect, handleClose, customEnterHandler, customSpaceHandler, additionalKeys, enableSpace, includeHighlightedOnEnter, selectedItems]);
};

/**
 * ฟังก์ชันสำหรับเลื่อน scroll ไปยังแถวที่เลือก
 * @param {number} rowIndex - index ของแถว
 */
export const scrollToRow = (rowIndex) => {
    setTimeout(() => {
        // ลำดับความสำคัญในการค้นหา table container
        let tableContainer = null;
        let selectedRow = null;
        
        // 1. ค้นหาใน modal ก่อน (สำหรับ modal)
        tableContainer = document.querySelector('.modal.show .table-responsive');
        if (tableContainer) {
            selectedRow = document.querySelector(`.modal.show tbody tr:nth-child(${rowIndex + 1})`);
        }
        
        // 2. ถ้าไม่เจอใน modal ให้ค้นหาใน main page (สำหรับหน้า InitialData)
        if (!tableContainer || !selectedRow) {
            tableContainer = document.querySelector('.table-responsive');
            selectedRow = document.querySelector(`tbody tr:nth-child(${rowIndex + 1})`);
        }
        
        // 3. ถ้ายังไม่เจอ ลองค้นหาแบบ specific ของ InitialData
        if (!tableContainer || !selectedRow) {
            tableContainer = document.querySelector('#basic-datatables')?.closest('.table-responsive');
            selectedRow = document.querySelector(`#basic-datatables tbody tr:nth-child(${rowIndex + 1})`);
        }
        
        if (tableContainer && selectedRow) {
            // คำนวณตำแหน่งที่ต้องเลื่อน
            const rowTop = selectedRow.offsetTop;
            const rowHeight = selectedRow.offsetHeight;
            const containerHeight = tableContainer.clientHeight;
            const scrollTop = tableContainer.scrollTop;
            
            // ตรวจสอบว่าแถวอยู่นอกเขตมองเห็นหรือไม่
            if (rowTop < scrollTop) {
                // แถวอยู่เหนือเขตมองเห็น - เลื่อนขึ้น
                tableContainer.scrollTop = Math.max(0, rowTop - 20); // เผื่อ padding
            } else if (rowTop + rowHeight > scrollTop + containerHeight) {
                // แถวอยู่ใต้เขตมองเห็น - เลื่อนลง
                tableContainer.scrollTop = rowTop - containerHeight + rowHeight + 20; // เผื่อ padding
            }
        }
    }, 10);
};

/**
 * ฟังก์ชันสำหรับสร้าง style ของแถวใน modal
 * @param {number} index - index ของแถว
 * @param {number} selectedRowIndex - index ของแถวที่เลือก
 * @param {boolean} isSelected - สถานะการเลือก (สำหรับ checkbox modal)
 * @returns {Object} - style object
 */
export const getRowStyle = (index, selectedRowIndex, isSelected = false) => {
    const baseStyle = {
        cursor: 'pointer',
        transition: 'background-color 0.3s ease',
    };

    if (index === selectedRowIndex || isSelected) {
        baseStyle.backgroundColor = 'rgb(105, 188, 216)';
    } else {
        baseStyle.backgroundColor = 'transparent';
    }

    return baseStyle;
};

/**
 * ฟังก์ชันสำหรับสร้าง keyboard instruction text
 * @param {string} itemName - ชื่อรายการ (เช่น 'สินค้า', 'พนักงาน', 'บริษัท')
 * @param {boolean} hasSpace - แสดงคำแนะนำ Space key หรือไม่
 * @returns {string} - คำแนะนำการใช้คีย์บอร์ด
 */
export const getKeyboardInstructions = (itemName, hasSpace = false) => {
    const baseInstructions = `คีย์บอร์ด: F5 (ค้นหา) | ↑↓ (เลือก${itemName}) | Enter (ยืนยัน) | ESC (ปิด)`;
    
    if (hasSpace) {
        return `คีย์บอร์ด: F5 (ค้นหา) | ↑↓ (เลือก${itemName}) | Space (เลือก/ยกเลิก) | Enter (ยืนยัน) | ESC (ปิด)`;
    }
    
    return baseInstructions;
};

/**
 * Hook สำหรับให้ลูกศรบนคีย์บอร์ด เลื่อน Focus ระหว่างฟิลด์ภายใน Modal Form
 * ใช้ได้กับ input, textarea, select, button และ elements ที่มี tabindex
 * @param {boolean} showModal - สถานะการเปิด Modal
 */
export const useFormFieldArrowNavigation = (showModal) => {
    useEffect(() => {
        const isVisible = (el) => {
            if (!el) return false;
            const style = window.getComputedStyle(el);
            return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
        };

        const getFocusable = (container) => {
            if (!container) return [];
            const selector = 'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
            return Array.from(container.querySelectorAll(selector)).filter(isVisible);
        };

        const handleKey = (event) => {
            if (!showModal) return;

            const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
            if (!arrowKeys.includes(event.key)) return;

            // หาคอนเทนเนอร์ modal ที่กำลังแสดงอยู่ (เลือกตัวสุดท้ายถ้ามีหลายตัว)
            const modals = Array.from(document.querySelectorAll('.modal.show'));
            const modal = modals.length ? modals[modals.length - 1] : null;
            if (!modal) return;

            const focusables = getFocusable(modal);
            if (!focusables || focusables.length === 0) return;

            const active = document.activeElement;
            // ถ้า focus อยู่นอก modal ให้ focus ตัวแรก
            const index = focusables.indexOf(active);

            event.preventDefault();
            event.stopPropagation();

            let newIndex = 0;
            if (index === -1) {
                // ยังไม่มี focus ใน modal -> focus ตัวแรก
                newIndex = 0;
            } else {
                if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                    newIndex = index < focusables.length - 1 ? index + 1 : 0;
                } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                    newIndex = index > 0 ? index - 1 : focusables.length - 1;
                }
            }

            const toFocus = focusables[newIndex];
            if (toFocus) {
                // ถ้าเป็น select ให้ focus แล้วเปิด dropdown โดยเบราว์เซอร์อาจไม่เปิดอัตโนมัติ
                try {
                    toFocus.focus();
                } catch (err) {
                    // ignore
                }
            }
        };

        window.addEventListener('keydown', handleKey, true);
        return () => window.removeEventListener('keydown', handleKey, true);
    }, [showModal]);
};
